import React, { useState, useRef, useEffect } from 'react';
import { Meal, MealType } from '../lib/types';
import { formatIsoDate, getDefaultMealType, createIsoFromLocalDateAndTime } from '../lib/dateUtils';
import { compressImage } from '../lib/imageUtils';
import { canonicalizeIngredient, decomposeCompositeDish } from '../lib/ingredientUtils';
import { localStore } from '../lib/storage';
import { autoRegisterDiscoveredFoods } from '../lib/fodmapEngine';
import { analyzeImageDirectOrProxy } from '../lib/gemini';
import {
  X,
  Camera,
  Image as ImageIcon,
  Sparkles,
  RefreshCw,
  Loader2,
  Clock,
  Calendar,
  Check,
  Plus,
  Sunrise,
  Sun,
  Moon,
  Coffee,
  Apple,
} from 'lucide-react';

interface PhotoLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveMeal: (meal: Omit<Meal, 'id'>) => void;
}

const MEAL_TYPES: { type: MealType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { type: 'Breakfast', label: 'Breakfast', icon: Sunrise },
  { type: 'Lunch', label: 'Lunch', icon: Sun },
  { type: 'Dinner', label: 'Dinner', icon: Moon },
  { type: 'Snack', label: 'Snack', icon: Apple },
  { type: 'Drink', label: 'Drink', icon: Coffee },
];

export const PhotoLogModal: React.FC<PhotoLogModalProps> = ({ isOpen, onClose, onSaveMeal }) => {
  const [mealType, setMealType] = useState<MealType>(() => getDefaultMealType());
  const [description, setDescription] = useState('');
  const [recipeServings, setRecipeServings] = useState<number>(1);
  const [portionsEaten, setPortionsEaten] = useState<number>(1);
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [newIngredientInput, setNewIngredientInput] = useState('');
  const [notes, setNotes] = useState('');

  const now = new Date();
  const [entryDate, setEntryDate] = useState(() => formatIsoDate(now));
  const [entryTime, setEntryTime] = useState(() => {
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setMealType(getDefaultMealType());
      // Prompt camera on open if no photo selected yet
      setTimeout(() => {
        if (!photoPreview && cameraInputRef.current) {
          cameraInputRef.current.click();
        }
      }, 150);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsScanning(true);
      setStatusMessage('Optimizing photo for vision AI...');
      const compressed = await compressImage(file, 900, 0.82);
      setPhotoPreview(compressed);
      await scanPhotoWithAI(compressed);
    } catch (err) {
      console.warn('Failed to process photo:', err);
      setStatusMessage('Photo selected');
    } finally {
      setIsScanning(false);
      if (e.target) e.target.value = '';
    }
  };

  const scanPhotoWithAI = async (base64Img: string) => {
    setIsScanning(true);
    setStatusMessage('Analyzing dish and ingredients...');

    try {
      const data = await analyzeImageDirectOrProxy(base64Img, description);

      if (data.success) {
        const category = data.suggested_category;
        if (category && ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Drink'].includes(category)) {
          setMealType(category as MealType);
        }

        const suggestedDishName = data.suggested_name || data.dish_name;
        if (suggestedDishName && !description && !/^(AI\s*SCANNED\s*PLATE|SCANNED\s*PLATE|PLATE|MEAL|AI\s*PLATE)$/i.test(suggestedDishName)) {
          setDescription(suggestedDishName);
        }

        let rawIngredients: string[] = [];
        if (Array.isArray(data.ingredients_tags) && data.ingredients_tags.length > 0) {
          rawIngredients = data.ingredients_tags
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || i?.title || '')))
            .filter(Boolean);
        } else if (Array.isArray(data.ingredients) && data.ingredients.length > 0) {
          rawIngredients = data.ingredients
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || i?.title || '')))
            .filter(Boolean);
        }

        // Auto-register any novel ingredients discovered by AI into local & global database
        if (Array.isArray(data.discovered_foods) && data.discovered_foods.length > 0) {
          autoRegisterDiscoveredFoods(data.discovered_foods).catch(() => {});
        } else if (Array.isArray(data.ingredients) && data.ingredients.length > 0) {
          autoRegisterDiscoveredFoods(data.ingredients).catch(() => {});
        } else if (rawIngredients.length > 0) {
          autoRegisterDiscoveredFoods(rawIngredients).catch(() => {});
        }

        if (rawIngredients.length > 0) {
          const cleanList = rawIngredients
            .filter((t: string) => !/may contain|trace/i.test(t))
            .filter((t: string) => !/^(water|tap water|salt|table salt|sea salt|black pepper|pepper|cooking spray)$/i.test(t.trim()))
            .map((t: string) => canonicalizeIngredient(t))
            .filter(Boolean) as string[];
          const uniqueList: string[] = Array.from(new Set(cleanList));
          setIngredients(uniqueList);
          if (!description && uniqueList.length > 0 && !suggestedDishName) {
            setDescription(uniqueList.slice(0, 2).join(' & '));
          }
          setStatusMessage(`Identified: ${suggestedDishName ? `"${suggestedDishName}" with ` : ''}${uniqueList.length} ingredient${uniqueList.length === 1 ? '' : 's'}.`);
        } else {
          setStatusMessage(suggestedDishName ? `Identified "${suggestedDishName}".` : 'Photo analyzed.');
        }

        const notesText = data.notes;
        if (notesText && !notes) {
          setNotes(notesText);
        }
      } else {
        setStatusMessage(data.error || 'AI vision service unavailable. You can type ingredients manually.');
      }
    } catch (err: any) {
      console.warn('Photo AI scan failed:', err);
      const isTimeout = err?.name === 'AbortError';
      setStatusMessage(
        isTimeout
          ? 'Analysis timed out. Please try again or type ingredients manually.'
          : 'Could not auto-detect ingredients. You can add them manually.'
      );
    } finally {
      setIsScanning(false);
    }
  };

  const handleAddIngredient = () => {
    const trimmed = newIngredientInput.trim();
    if (!trimmed) return;
    const canonical = canonicalizeIngredient(trimmed);
    if (!ingredients.includes(canonical)) {
      setIngredients([...ingredients, canonical]);
    }
    setNewIngredientInput('');
  };

  const [scrollY, setScrollY] = useState(0);

  const handleResetInputs = () => {
    setPhotoPreview(null);
    setDescription('');
    setIngredients([]);
    setNewIngredientInput('');
    setNotes('');
    setStatusMessage(null);
    setScrollY(0);
    const now = new Date();
    setEntryDate(formatIsoDate(now));
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setEntryTime(`${hh}:${mm}`);
    setMealType(getDefaultMealType());
  };

  const handleRemoveIngredient = (ing: string) => {
    setIngredients(ingredients.filter((i) => i !== ing));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalDescription = description.trim() || 'Photo Logged Meal';
    const mult = Math.max(0.01, Math.round((portionsEaten / recipeServings) * 1000) / 1000);

    onSaveMeal({
      date: entryDate,
      timestamp_iso: createIsoFromLocalDateAndTime(entryDate, entryTime),
      meal_type: mealType,
      description: finalDescription,
      portionMultiplier: mult,
      recipeServings: recipeServings,
      portionsEaten: portionsEaten,
      ingredients_tags: ingredients.join(', '),
      notes: undefined,
    });

    onClose();
  };

  const bannerHeight = photoPreview ? Math.max(0, 220 - scrollY) : 0;
  const bannerOpacity = photoPreview ? Math.max(0, 1 - scrollY / 180) : 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex flex-col justify-end animate-fade-in font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]">
      <div className="bg-white text-slate-900 rounded-t-[32px] sm:rounded-2xl max-h-[92vh] flex flex-col w-full max-w-md mx-auto border-t sm:border border-slate-200 shadow-2xl overflow-hidden">
        {/* Drag Handle */}
        <div className="w-full pt-3 pb-1 flex justify-center bg-white">
          <div className="w-10 h-1.5 rounded-full bg-slate-300" />
        </div>

        {/* Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-100 bg-white">
          <div className="flex items-center space-x-2">
            <Camera className="w-4 h-4 text-slate-900" />
            <h2 className="text-base font-black uppercase tracking-tight text-slate-900">AI Photo Log</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleResetInputs}
              className="text-[11px] font-bold text-slate-400 hover:text-rose-500 transition-colors px-1.5 py-1"
            >
              Reset Inputs
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:text-rose-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Speed-dependent Collapsible Image Banner (reappears when scrolling back up) */}
        {photoPreview && (
          <div
            style={{
              height: `${bannerHeight}px`,
              opacity: bannerOpacity,
              transition: 'height 0.05s ease-out, opacity 0.05s ease-out',
            }}
            className="w-full overflow-hidden relative shrink-0 bg-slate-100"
          >
            <img
              src={photoPreview}
              alt="Captured meal"
              className="w-full h-full object-cover rounded-none"
            />
            {isScanning && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex flex-col items-center justify-center text-slate-900 space-y-1.5 p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-900" />
                <span className="text-xs font-bold">{statusMessage || 'AI analyzing photo...'}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="absolute top-2.5 right-2.5 px-3 py-1.5 rounded-xl bg-white/90 hover:bg-white text-slate-900 text-[11px] font-bold shadow-md active:scale-95 transition-all border border-slate-200"
            >
              Retake
            </button>
          </div>
        )}

        {/* Form Body with onScroll for speed-dependent collapse/expand */}
        <form
          onSubmit={handleSubmit}
          onScroll={(e) => setScrollY(e.currentTarget.scrollTop)}
          className="p-5 overflow-y-auto space-y-4 flex-1 text-left bg-white"
        >
          {/* Photo Capture Buttons (shown when no photo yet) */}
          {!photoPreview && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="py-6 px-4 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex flex-col items-center justify-center space-y-2 text-slate-800 active:scale-95 transition-all shadow-xs"
                >
                  <Camera className="w-6 h-6 text-slate-700" />
                  <span className="text-xs font-bold text-slate-900">Take Photo</span>
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="py-6 px-4 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex flex-col items-center justify-center space-y-2 text-slate-800 active:scale-95 transition-all shadow-xs"
                >
                  <ImageIcon className="w-6 h-6 text-slate-700" />
                  <span className="text-xs font-bold text-slate-900">Upload Photo</span>
                </button>
              </div>

              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
            </div>
          )}
          {/* Meal Category Pills (Auto-selected, user can change) */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">
              Meal Category (Auto-Selected)
            </span>
            <div className="grid grid-cols-5 gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200">
              {MEAL_TYPES.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMealType(type)}
                  className={`py-1.5 px-1 rounded-lg text-xs font-bold flex flex-col items-center justify-center space-y-0.5 transition-all ${
                    mealType === type
                      ? 'bg-slate-900 text-white shadow-xs font-black'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[9px]">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dish Name */}
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Dish Name
            </label>
            <input
              type="text"
              required
              placeholder="e.g., Grilled Chicken Salad"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-slate-400"
            />
          </div>

          {/* Recipe Yield & Portion Eaten Controls */}
          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Recipe Yield & Portion Eaten
              </span>
              <span className="text-[10px] font-bold text-slate-700">
                {portionsEaten} of {recipeServings} {recipeServings === 1 ? 'serv' : 'servs'} ({Math.round((portionsEaten / recipeServings) * 1000) / 10}%)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                  Recipe Serves
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setRecipeServings(Math.max(1, recipeServings - 1))}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    -
                  </button>
                  <span className="flex-1 text-center font-bold text-slate-900 text-xs">
                    {recipeServings} {recipeServings === 1 ? 'serv' : 'servs'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRecipeServings(recipeServings + 1)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[1, 2, 4, 6, 8].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRecipeServings(s)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                        recipeServings === s
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                  Portion You Ate
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPortionsEaten(Math.max(0.25, Math.round((portionsEaten - 0.5) * 100) / 100))}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    -
                  </button>
                  <span className="flex-1 text-center font-bold text-slate-900 text-xs">
                    {portionsEaten} {portionsEaten === 1 ? 'portion' : 'portions'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPortionsEaten(Math.round((portionsEaten + 0.5) * 100) / 100)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[0.5, 1, 1.5, 2, recipeServings].filter((v, idx, arr) => arr.indexOf(v) === idx).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPortionsEaten(p)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                        portionsEaten === p
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      {p === recipeServings && p > 1 ? 'All' : `${p}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Simple Ingredients Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Ingredients ({ingredients.length})
              </label>
              <span className="text-[9px] text-slate-400">Tap to remove</span>
            </div>

            {/* Ingredients Tags */}
            <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2.5 bg-slate-50 rounded-2xl border border-slate-200">
              {ingredients.map((ing) => (
                <button
                  key={ing}
                  type="button"
                  onClick={() => handleRemoveIngredient(ing)}
                  className="px-2.5 py-1 rounded-xl bg-white border border-slate-200 hover:bg-rose-50 text-slate-800 hover:text-rose-600 text-xs font-bold flex items-center space-x-1 active:scale-95 transition-all shadow-2xs"
                >
                  <span>{ing}</span>
                  <span className="text-slate-400 hover:text-rose-500">×</span>
                </button>
              ))}
              {ingredients.length === 0 && (
                <span className="text-xs text-slate-400 p-1">No ingredients added yet</span>
              )}
            </div>

            {/* Add ingredient input */}
            <div className="flex items-center space-x-1.5">
              <input
                type="text"
                placeholder="Add ingredient..."
                value={newIngredientInput}
                onChange={(e) => setNewIngredientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddIngredient();
                  }
                }}
                className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-slate-400"
              />
              <button
                type="button"
                onClick={handleAddIngredient}
                disabled={!newIngredientInput.trim()}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs active:scale-95 disabled:opacity-40 transition-all shadow-xs"
              >
                + Add
              </button>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Date
              </label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Time
              </label>
              <input
                type="time"
                value={entryTime}
                onChange={(e) => setEntryTime(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none"
              />
            </div>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={!description.trim() || isScanning}
            className="w-full py-3.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-wider shadow-md active:scale-[0.98] disabled:opacity-40 transition-all mt-2"
          >
            Save Meal
          </button>
        </form>
      </div>
    </div>
  );
};
